import React, { useEffect, useRef, useState } from 'react';
import type { AdConsent } from '../ads';

/** Contextual-only by default. A real CMP may take over the cancelable app event. */
export const AdPreferences: React.FC = () => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [withdrawn, setWithdrawn] = useState(false);

  useEffect(() => {
    const setConsent = (consent: AdConsent) => window.PicPlayerAds?.setConsent(consent);
    const open = (event: Event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      const delegated = new CustomEvent('storyboardsmith:ad-preferences', { cancelable: true });
      if (!document.dispatchEvent(delegated)) return;
      returnFocus.current = document.activeElement as HTMLElement;
      setWithdrawn(false);
      if (!dialogRef.current?.open) dialogRef.current?.showModal();
    };
    const update = (event: Event) => {
      const detail = (event as CustomEvent<AdConsent>).detail;
      if (detail && typeof detail.ads === 'boolean' && Array.isArray(detail.networkIds)) setConsent(detail);
    };
    const api = { setConsent, openPreferences: () => document.dispatchEvent(new CustomEvent('picplayer:ad-preferences', { cancelable: true })) };
    window.StoryboardSmithAds = api;
    document.addEventListener('picplayer:ad-preferences', open);
    document.addEventListener('storyboardsmith:ad-consent', update);
    return () => {
      document.removeEventListener('picplayer:ad-preferences', open);
      document.removeEventListener('storyboardsmith:ad-consent', update);
      if (window.StoryboardSmithAds === api) delete window.StoryboardSmithAds;
    };
  }, []);
  const close = () => { dialogRef.current?.close(); returnFocus.current?.focus(); };
  return <dialog ref={dialogRef} className="ss-ad-dialog" aria-labelledby="ad-preferences-title" onClose={() => returnFocus.current?.focus()} onClick={event => { if (event.target === event.currentTarget) close(); }}>
    <div><h2 id="ad-preferences-title">Ad choices</h2>
      <p>StoryboardSmith uses PicPlayer’s shared ad manager. Advertising starts in contextual-only mode, without permission for consent-required networks.</p>
      <p>Your comic title, scenes, uploaded artwork and exported files are not sent to the ad manager. Ad requests do send the app ID, placement and consent settings; the service can see normal connection information such as your IP address.</p>
      <p>A connected consent platform can offer additional choices. This dialog does not grant permission to any network.</p>
      <p role="status">{withdrawn ? 'Optional advertising consent has been withdrawn for this session.' : 'You can withdraw optional advertising consent below.'}</p>
      <div className="ss-ad-dialog-actions"><button type="button" className="secondary-button" onClick={() => { window.StoryboardSmithAds?.setConsent({ ads: false, networkIds: [] }); setWithdrawn(true); }}>Use contextual ads only</button><button type="button" className="primary-button" onClick={close}>Done</button></div>
      <a href={window.StoryboardSmithConfig?.privacyUrl || './privacy.html'} target="_blank" rel="noopener noreferrer">Read the privacy information</a>
    </div>
  </dialog>;
};
